import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartData, ChartConfiguration } from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { BaseChartDirective } from 'ng2-charts';
import { Subject, takeUntil } from 'rxjs';

import { FormatedPeopleDistribution } from '@admin/statistics/statistics.model';
import { PEOPLE_COLORS } from '@app/admin/statistics/statistics.constants';
import { getMaxDataValue } from '@app/shared/utils/chart.utils';

@Component({
  selector: 'flow-age-gender-distribution-bar-chart',
  templateUrl: './age-gender-distribution-bar-chart.component.html',
  styleUrls: ['./age-gender-distribution-bar-chart.component.scss'],
})
export class AgeGenderDistributionBarChartComponent implements OnInit, OnChanges, OnDestroy {
  destroy$ = new Subject<void>();
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  @Input() data!: FormatedPeopleDistribution;
  @Input() dataCompare!: FormatedPeopleDistribution;
  @Input() compare!: boolean;
  @Input() weekNumber!: number;
  @Input() weekNumberCompare!: number;
  @Input() timeframeType!: string;
  @Input() hasData: boolean = false;

  chartPlugins = [DataLabelsPlugin];
  barChartData: ChartData<'bar', number[], string | string[]> = null;
  barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    scales: {
      x: {
        type: 'category',
        title: {
          display: false,
        },
        grid: {
          display: true,
          tickColor: 'white',
        },
      },
      y: {
        display: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: function (context) {
          if (context.dataIndex === 7 && (context.datasetIndex === 1 || context.datasetIndex === 3)) {
            return false;
          } else {
            if (context.dataset.data[context.dataIndex] !== 0) {
              return true;
            } else {
              return false;
            }
          }
        },
        padding: 0,
        rotation: -90,
        color: 'black',
        anchor: 'end',
        align: 'top',
        formatter: value => value,
      },
      tooltip: {
        callbacks: {
          label: context => {
            const datasetIndex = context.datasetIndex;
            const index = context.dataIndex;
            const dataset = context.chart.data.datasets[datasetIndex];

            if (index === dataset.data.length - 1) {
              return `${'OTS'}: ${context.parsed.y}`;
            }
            return `${dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
  };

  labels = [
    'peopleCount.targetGroups.under18',
    'peopleCount.targetGroups.18-24',
    'peopleCount.targetGroups.25-34',
    'peopleCount.targetGroups.35-44',
    'peopleCount.targetGroups.45-54',
    'peopleCount.targetGroups.55-64',
    'peopleCount.targetGroups.65+',
    'peopleCount.targetGroups.opportunityToSee',
  ];

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.chart) {
        this.setBarChart(this.data, this.dataCompare);
        this.chart.update();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentValue = changes.data?.currentValue;
    const currentValueCompare = changes.dataCompare?.currentValue;

    if (currentValue) {
      this.setBarChart(currentValue, currentValueCompare || this.dataCompare);
    }
  }

  updateBarChartGrace(): void {
    const maxDataValue = getMaxDataValue(this.barChartData);
    const valueCharLength = maxDataValue.toString().length;
    const percentage = valueCharLength * 15;
    this.barChartOptions.scales!.y!['grace'] = `${percentage}%`;
  }

  setBarChart(data: FormatedPeopleDistribution, dataCompare: FormatedPeopleDistribution) {
    const dataMen = this.setDataSet('peopleCount.men', data?.menArray, PEOPLE_COLORS?.men, PEOPLE_COLORS?.ots, this.weekNumber);
    const dataSetWoman = this.setDataSet('peopleCount.women', data?.womenArray, PEOPLE_COLORS?.women, PEOPLE_COLORS?.ots, this.weekNumber);
    const dataCompareMen = this.setDataSet(
      'peopleCount.men',
      dataCompare?.menArray,
      PEOPLE_COLORS?.menCompare,
      PEOPLE_COLORS?.otsCompare,
      this.weekNumberCompare
    );
    const dataCompareWoman = this.setDataSet(
      'peopleCount.women',
      dataCompare?.womenArray,
      PEOPLE_COLORS?.womenCompare,
      PEOPLE_COLORS?.otsCompare,
      this.weekNumberCompare
    );

    this.barChartData = {
      labels: this.labels.map(label => this.translateService.instant(label)),
      datasets: [dataMen, dataSetWoman, ...(this.compare ? [dataCompareMen, dataCompareWoman] : [])],
    };

    this.updateBarChartGrace();
  }
  setDataSet(label: string, data: number[], color: string, otsColor?: string, weekNumber?: number) {
    const colorOts = data
      ? data.map((_, index) => {
          return index === data.length - 1 ? otsColor : color;
        })
      : PEOPLE_COLORS?.white;

    const dataSet = {
      label: this.translateService.instant(label),
      data,
      fill: 'origin',
      backgroundColor: colorOts,
      hoverBackgroundColor: colorOts,
      borderColor: colorOts,
      pointBackgroundColor: PEOPLE_COLORS?.white,
      categoryPercentage: 0.7,
      barPercentage: 1,
    };

    if (this.compare) {
      dataSet.label = `${this.translateService.instant(label)}, ${this.translateService.instant('peopleCount.week')} ${weekNumber}`;
    }

    return dataSet;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
